import React, { FC, useEffect } from 'react';
import type { StepPageProps } from '../../../assessment-portal';
import { useAssessmentPortalState } from '../../../assessment-portal';
import useBookingSubmission from '../../hooks/use-booking-submission';
import useFeeQuote from '../../hooks/use-fee-quote';
import { hasMedicareOrDvaCard } from '../../utils/fee';
import { AdhdFlowStep } from '../../data/constants';
import BookingPage from '../booking-page';
import type { AdhdFlowState, BookingValues } from '../../types/flow';

const BookingStep: FC<StepPageProps> = ({ defaultValues }) => {
  const { flowState, updateFlowState, goToStep } = useAssessmentPortalState<AdhdFlowState>();
  const feeQuote = useFeeQuote(
    flowState.patientInfo.state,
    flowState.patientInfo.medicareType,
    flowState.fee,
  );
  const feeEstimate = feeQuote.quote;

  const {
    state,
    confirmedRef,
    timingRef,
    markSlotSelected,
    handleSubmitBooking,
    resetSubmitError,
  } = useBookingSubmission(flowState, feeEstimate);

  const feeChanged = state.status === 'error' && state.failure.kind === 'fee-changed';
  const reloadFeeQuote = feeQuote.reload;
  useEffect(() => {
    if (feeChanged) reloadFeeQuote();
  }, [feeChanged, reloadFeeQuote]);

  return (
    <BookingPage
      defaultValues={defaultValues}
      submitting={state.status === 'submitting'}
      bookingConfirmedRef={confirmedRef}
      bookingTimingRef={timingRef}
      onSlotSelected={markSlotSelected}
      failure={state.status === 'error' ? state.failure : undefined}
      feeEstimate={feeEstimate}
      feeLoading={feeQuote.status === 'loading'}
      hasMedicareCard={hasMedicareOrDvaCard(flowState.patientInfo.medicareType)}
      feeUnavailable={feeQuote.status === 'error'}
      onRetryFee={feeQuote.reload}
      feePrescriptionType={flowState.fee.prescriptionType}
      feeLocation={flowState.fee.location}
      feeAssessmentType={flowState.fee.feeAssessmentScope}
      feeSeenPsychiatrist={flowState.fee.seenPsychiatrist}
      onBack={() => goToStep(AdhdFlowStep.MEDICAL_HISTORY)}
      onSubmit={(values: BookingValues) => {
        resetSubmitError();
        updateFlowState('booking', values);
        handleSubmitBooking(values, () => {
          updateFlowState('submissionResult', { isSuccess: true, errorMessage: '' });
          goToStep(AdhdFlowStep.BOOKING_CONFIRMATION);
        });
      }}
    />
  );
};

export default BookingStep;
